import React, { useEffect, useRef, useState } from 'react';
import { Home, Loader2, Ban, ArrowLeft, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { C } from '../shared';
import { ModuleBlock } from '../ModuleBlock';
import { stepsFor, modulesFor, missingIn } from './steps';
import { Stepper } from './Stepper';
import { AddressStep } from './AddressStep';
import { PropertyCard } from './PropertyCard';
import { DisclosureStep } from './DisclosureStep';
import { PriceStep } from './PriceStep';
import { BindStep } from './BindStep';
import { DocumentsStep } from './DocumentsStep';

export const RESIDENTIAL_PRODUCTS = { home_contents: 'ra-home-v1', residential_landlords: 'ra-landlord-v1' };
const today = () => new Date().toISOString().slice(0, 10);

export const ResidentialJourney = ({ api, classId, onReset }) => {
  const { user } = useAuth();
  const isStaff = ['platform_owner', 'mga_admin', 'underwriter'].includes(user?.role);
  const product = RESIDENTIAL_PRODUCTS[classId];
  const variant = classId === 'residential_landlords' ? 'landlord' : 'householder';
  const steps = stepsFor(variant);
  const [step, setStep] = useState(0);
  const [addr, setAddr] = useState(null);
  const [rendered, setRendered] = useState(null);
  const [answers, setAnswers] = useState({});
  const [confirmed, setConfirmed] = useState({});
  const [assumptions, setAssumptions] = useState({});
  const [acks, setAcks] = useState({});
  const [pending, setPending] = useState(false);
  const [rec, setRec] = useState(null);
  const [stale, setStale] = useState(false);
  const [selected, setSelected] = useState('as_quoted');
  const [form, setForm] = useState({ insured_name: '', insured_email: '', interested_party: '', inception_date: today(), override_reason: '' });
  const [bind, setBind] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const started = useRef(null);
  const timer = useRef(null);

  const onResolved = data => {
    setAddr(data); setAnswers({}); setConfirmed({}); setRec(null); setBind(null); setErr(null); setRendered(null);
    started.current = new Date().toISOString();
    api.get('/quote/residential/questions', { params: { product, pid: data.pid } }).then(({ data: r }) => setRendered(r))
      .catch(e => { console.error('API error:', e?.message); setErr(e?.response?.data?.detail || e?.message); });
  };

  const rerender = (a, c) => {
    clearTimeout(timer.current);
    setPending(true); setStale(!!rec);
    timer.current = setTimeout(() => {
      api.post('/quote/residential/questions', { product, pid: addr.pid, answers: a, confirmed: Object.keys(c).filter(k => c[k]) })
        .then(({ data }) => setRendered(data)).catch(e => console.error('API error:', e?.message)).finally(() => setPending(false));
    }, 450);
  };
  const setAnswer = (qid, v) => { const a = { ...answers, [qid]: v }; setAnswers(a); rerender(a, confirmed); };
  const confirm = qid => { const c = { ...confirmed, [qid]: true }; setConfirmed(c); rerender(answers, c); };
  const change = qid => setConfirmed({ ...confirmed, [qid]: false });

  const price = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post('/quote/residential', { product, pid: addr.pid, answers, confirmed: Object.keys(confirmed).filter(k => confirmed[k]), assumptions, inception_date: form.inception_date });
      setRec(data); setStale(false); setSelected('as_quoted');
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'object' ? `${d.message} (${(d.missing || []).join(', ')})` : d || e?.message);
    }
    setBusy(false);
  };
  useEffect(() => { if (steps[step]?.id === 'price' && !rec && rendered && !busy) price(); }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const doBind = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post(`/quote/residential/${rec.quote_id}/bind`, { option_id: selected, insured_name: form.insured_name, insured_email: form.insured_email || null,
        interested_party: form.interested_party || null, inception_date: form.inception_date, address_label: addr.label, acknowledgements: acks,
        override_reason: form.override_reason || null, journey_started_at: started.current });
      setBind(data); setStep(steps.length - 1);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'object' ? d.message : d || e?.message);
    }
    setBusy(false);
  };

  const ko = rendered?.knockouts_fired?.find(k => k.stop);
  const cur = steps[step];
  const stepModules = modulesFor(rendered, cur);
  const missing = missingIn(rendered, cur);
  const acksOk = acks.duty_to_take_reasonable_care && acks.documents_received;
  const priced = rec && rec.options?.length > 0 && !stale;
  const canNext = cur.id === 'address' ? !!addr && !!rendered : cur.id === 'disclosure' ? !ko && !missing.length && acksOk
    : cur.id === 'price' ? !!priced : cur.id === 'bind' ? !!bind : cur.id === 'documents' ? false : !ko && !missing.length;
  const reachable = i => i <= step || (i === step + 1 && canNext) || (bind && i === steps.length - 1);

  return (
    <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="residential-journey" style={{ borderLeft: `3px solid ${C.blue}` }}>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2" style={{ color: C.navy }}><Home className="w-4 h-4" style={{ color: C.blue }} /> Family 6 · {rendered?.product?.label || (variant === 'landlord' ? 'Rural Affinity Landlord' : 'Rural Affinity Home')}</h2>
          <p className="text-xs mt-0.5" style={{ color: C.slate }}>{cur.hint}</p>
        </div>
        <span className="flex items-center gap-2 flex-wrap">
          {rendered && <span className="text-[10px] font-semibold px-2 py-1 rounded flex items-center gap-1" style={{ background: 'rgba(26,76,124,0.1)', color: C.blue }} data-testid="rj-typed-chip">{pending && <Loader2 className="w-3 h-3 animate-spin" />}typed so far: {rendered.typed_inputs} · ≤ {rendered.budget.typed_inputs_max}</span>}
          <span className="text-[10px] font-semibold px-2 py-1 rounded uppercase" style={{ background: C.bg, color: C.navy }} data-testid="rj-mode-chip">{rendered?.mode || 'DEMO · free data'}</span>
        </span>
      </div>
      <Stepper steps={steps} current={step} onJump={setStep} reachable={reachable} />
      {rendered?.facts && cur.id !== 'documents' && <p className="mt-3 text-[10px]" style={{ color: C.slate }} data-testid="rj-facts">known after the address: {Object.entries(rendered.facts).filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => `${k} ${v}`).join(' · ')}</p>}
      {ko && (
        <p className="mt-3 text-[11px] font-semibold flex items-center gap-1.5 rounded-sm p-2" style={{ color: ko.action === 'decline' ? C.red : C.amber, background: 'rgba(226,75,74,0.05)' }} data-testid="rj-stop-banner">
          <Ban className="w-3.5 h-3.5" /> {ko.action} at “{ko.stage}” — {ko.reason}. Clears when {ko.clears_when}. Nothing further is called.
        </p>
      )}
      <div className="mt-4">
        {cur.id === 'address' && <AddressStep api={api} addr={addr} onResolved={onResolved} />}
        {cur.id === 'property' && addr && <PropertyCard api={api} pid={addr.pid} rebuild={rendered?.rebuild_prefill} />}
        {cur.id === 'cover' && addr && !rendered?.rebuild_prefill && <p className="text-[10px] rounded-sm p-2" style={{ color: C.amber, background: 'rgba(178,117,22,0.06)' }} data-testid="rj-rebuild-unavailable">No indicative rebuild for this parcel — the free registers publish no floor area here (the licensed source would be Cordell / a footprint layer), so the building sum insured is typed rather than prefilled.</p>}
        {['property', 'tenancy', 'cover'].includes(cur.id) && stepModules.map(m => <ModuleBlock key={m.name} m={m} answers={answers} confirmed={confirmed} setAnswer={setAnswer} confirm={confirm} change={change} />)}
        {cur.id === 'disclosure' && <DisclosureStep modules={stepModules} answers={answers} confirmed={confirmed} setAnswer={setAnswer} confirm={confirm} change={change} acks={acks} setAcks={setAcks} />}
        {cur.id === 'price' && <PriceStep rec={rec} busy={busy} err={err} onPrice={price} selected={selected} onSelect={setSelected} isStaff={isStaff} assumptions={assumptions} setAssumptions={setAssumptions} stale={stale} api={api} onRepriced={setRec} />}
        {cur.id === 'bind' && <BindStep rec={rec} option={rec?.options?.find(o => o.id === selected)} form={form} setForm={setForm} acks={acks} isStaff={isStaff} busy={busy} err={err} onBind={doBind} stale={stale} />}
        {cur.id === 'documents' && <DocumentsStep bind={bind} isStaff={isStaff} onReset={onReset} />}
      </div>
      {cur.id !== 'documents' && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap border-t border-[rgba(136,150,168,0.15)] pt-3">
          <Button variant="outline" size="sm" onClick={() => (step === 0 ? onReset() : setStep(step - 1))} className="rounded-sm text-xs" data-testid="rj-back-btn"><ArrowLeft className="w-3 h-3 mr-1" /> {step === 0 ? 'Back to families' : 'Back'}</Button>
          <span className="text-[10px]" style={{ color: C.amber }} data-testid="rj-missing-required">{missing.length > 0 && `required on this step: ${missing.join(', ')}`}</span>
          {cur.id !== 'bind' && <Button size="sm" onClick={() => setStep(step + 1)} disabled={!canNext} className="rounded-sm text-xs" style={{ background: canNext ? C.blue : C.slate }} data-testid="rj-next-btn">{cur.id === 'disclosure' ? 'Price' : cur.id === 'price' ? 'Bind this option' : 'Next'} <ArrowRight className="w-3 h-3 ml-1" /></Button>}
        </div>
      )}
    </Card>
  );
};
